import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Sparkles, Search, ArrowRight, ArrowUpRight, CheckCircle2, HelpCircle, Info } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO, { SITE_URL } from "../SEO";
import { Overline } from "../Reveal";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PROBLEMS, INDUSTRIES, USER_TYPES, PRODUCT_FAMILIES, PRODUCTS_KB, getProductKB } from "../../data/knowledge";
import { FAQ_SEGMENTS } from "../../data/faqs";
import { findSolutions, searchFaqs, productUrl } from "../../lib/finder";

const PATH = "/product-finder";

const EXAMPLES = [
  "I want to automate my online presence",
  "I run an agency and need software for clients",
  "I need a QR-based business solution",
  "I need to manage reviews",
  "I need to automate social media",
];

// Describes the page's own directory so the relationships on it are
// machine-readable too. Only lists things visible on the page.
const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${SITE_URL}${PATH}#page`,
  name: "MagicBolt AI Product Finder",
  url: `${SITE_URL}${PATH}`,
  description:
    "Describe a business problem and see which MagicBolt AI product solves it, why, and which features are involved - plus a directory of problems, industries and user types.",
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#organization` },
  mainEntity: {
    "@type": "ItemList",
    itemListElement: PRODUCTS_KB.filter((p) => p.status === "ACTIVE").map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.name,
      url: `${SITE_URL}${productUrl(p.id)}`,
    })),
  },
};

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? "bg-[#0B1533] text-white"
          : "border border-[#E6E9F1] bg-white text-[#475569] hover:border-[#FF6B00]/40 hover:text-[#0B1533]"
      }`}
    >
      {children}
    </button>
  );
}

function ProductLink({ id, className = "" }) {
  const kb = getProductKB(id);
  if (!kb) return null;
  return (
    <Link to={productUrl(id)} className={`font-semibold text-[#FF6B00] hover:underline ${className}`}>
      {kb.name}
    </Link>
  );
}

function ResultCard({ r, primary }) {
  return (
    <article
      data-testid="finder-result"
      className={`rounded-2xl border bg-white p-6 md:p-8 ${primary ? "border-[#FF6B00] mb-card-shadow-hover" : "border-[#E6E9F1] mb-card-shadow"}`}
    >
      {primary && (
        <span className="inline-block rounded-full bg-[#FF6B00] px-3 py-1 text-[10px] font-bold tracking-wide text-white">
          BEST MATCH
        </span>
      )}
      <h3 className="mt-3 text-xl md:text-2xl font-bold tracking-tight text-[#0B1533]">{r.name}</h3>
      <p className="mt-2 text-sm md:text-base text-[#475569] leading-relaxed">{r.definition}</p>

      <div className="mt-5">
        <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">Why it matches</h4>
        <ul className="mt-2 space-y-2">
          {r.reasons.map((reason, i) => (
            <li key={i} className="flex gap-2 text-sm text-[#475569]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
              <span>
                <span className="font-semibold text-[#0B1533]">{reason.problem}:</span> {reason.role}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {r.features.length > 0 && (
        <div className="mt-5">
          <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">Relevant features</h4>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {r.features.map((f) => (
              <li key={f.title} className="rounded-lg bg-[#F4F6FB] px-3 py-2 text-sm">
                <span className="font-semibold text-[#0B1533]">{f.title}</span>
                <span className="block text-xs text-[#64748B] mt-0.5">{f.desc}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.related.length > 0 && (
        <p className="mt-5 text-sm text-[#475569]">
          <span className="font-semibold text-[#0B1533]">Works with: </span>
          {r.related.map((kb, i) => (
            <span key={kb.id}>
              {i > 0 && ", "}
              <ProductLink id={kb.id} />
            </span>
          ))}
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button size="sm" className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg" asChild>
          <Link to={r.url}>
            {r.url === "/pricing" ? "See plans" : `Explore ${r.name}`} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Link>
        </Button>
        <Button size="sm" variant="outline" className="border-[#CBD2E0] bg-white text-[#0B1533] rounded-lg" asChild>
          <Link to="/pricing">Compare plans</Link>
        </Button>
      </div>
    </article>
  );
}

export default function ProductFinderPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [submitted, setSubmitted] = useState(params.get("q") || "");
  const [userTypeId, setUserTypeId] = useState(params.get("who") || null);
  const [industryId, setIndustryId] = useState(params.get("industry") || null);

  const syncUrl = (next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParams(p, { replace: true });
  };

  const run = (text) => {
    setQuery(text);
    setSubmitted(text);
    syncUrl({ q: text });
  };

  const hasInput = Boolean(submitted.trim() || userTypeId || industryId);
  const result = useMemo(
    () => (hasInput ? findSolutions({ query: submitted, industryId, userTypeId }) : null),
    [hasInput, submitted, industryId, userTypeId]
  );
  const faqs = useMemo(() => (submitted.trim() ? searchFaqs(submitted, FAQ_SEGMENTS) : []), [submitted]);
  const localBusiness = (result?.userType?.id || userTypeId) === "local-business";

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="product-finder-page">
      <SEO
        title="Product Finder - Which MagicBolt AI Tool Fits Your Need?"
        description="Describe what you need in plain words - reviews, social media, WhatsApp, QR menus or bookings - and see which MagicBolt AI product fits and why."
        path={PATH}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Product Finder", path: PATH },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>
      <Navbar />

      {/* Finder */}
      <section className="relative pt-32 pb-14 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">No product names needed</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#0B1533]">
              Find the right <span className="mb-gradient-text">MagicBolt AI</span> tool
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
              Tell us what you're trying to fix. We'll match it to the product that actually does it, show why,
              and list the features involved.
            </p>

            <form
              className="relative mt-8 max-w-xl mx-auto"
              onSubmit={(e) => {
                e.preventDefault();
                run(query);
              }}
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                data-testid="finder-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. I need more Google reviews for my salon"
                aria-label="Describe what you need"
                className="h-12 pl-11 pr-28 border-[#E6E9F1] bg-white focus-visible:ring-[#FF6B00] text-[#0B1533]"
              />
              <Button
                type="submit"
                size="sm"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#FF6B00] hover:bg-[#FF8533] text-white rounded-md"
              >
                Find
              </Button>
            </form>

            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => run(ex)}
                  className="rounded-full bg-[#FF6B00]/10 px-3 py-1 text-xs font-medium text-[#C2410C] hover:bg-[#FF6B00]/15"
                >
                  {ex}
                </button>
              ))}
            </div>

            <div className="mt-8 text-left">
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">I am a…</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {USER_TYPES.map((u) => (
                  <Chip
                    key={u.id}
                    testId="finder-user-type"
                    active={userTypeId === u.id}
                    onClick={() => {
                      const next = userTypeId === u.id ? null : u.id;
                      setUserTypeId(next);
                      syncUrl({ who: next });
                    }}
                  >
                    {u.name}
                  </Chip>
                ))}
              </div>
              <div className="mt-4 text-xs font-bold uppercase tracking-[0.15em] text-[#0B1533]">Industry (optional)</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {INDUSTRIES.map((ind) => (
                  <Chip
                    key={ind.id}
                    testId="finder-industry"
                    active={industryId === ind.id}
                    onClick={() => {
                      const next = industryId === ind.id ? null : ind.id;
                      setIndustryId(next);
                      syncUrl({ industry: next });
                    }}
                  >
                    {ind.name}
                  </Chip>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Results */}
      {result && (
        <section className="py-14" aria-live="polite" data-testid="finder-results">
          <div className="max-w-4xl mx-auto px-6">
            {result.products.length > 0 ? (
              <>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tighter text-[#0B1533]">
                  {result.basis === "industry"
                    ? `Commonly used by ${result.industry.name.toLowerCase()}`
                    : result.basis === "userType"
                    ? `Where a ${result.userType.name.toLowerCase()} usually starts`
                    : "Which product fits your need?"}
                </h2>
                {result.products.length > 1 && result.basis === "problem" && (
                  <p className="mt-2 text-sm text-[#475569]">
                    These products do different jobs and work together - each card explains its part.
                  </p>
                )}
                {localBusiness && (
                  <p className="mt-4 flex gap-2 rounded-xl border border-[#E6E9F1] bg-white p-4 text-sm text-[#475569]">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
                    <span>
                      MagicBolt AI is sold mainly to agencies and resellers, who set it up for local businesses under
                      their own brand. If you run a single business, <Link to="/contact" className="font-semibold text-[#FF6B00] hover:underline">contact us</Link> and
                      we'll point you to the right plan or a partner agency.
                    </span>
                  </p>
                )}
                <div className="mt-8 space-y-6">
                  {result.products.map((r, i) => (
                    <ResultCard key={r.id} r={r} primary={i === 0} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-10" data-testid="finder-no-match">
                <HelpCircle className="w-10 h-10 text-[#CBD2E0] mx-auto" />
                <p className="mt-4 text-[#475569] max-w-lg mx-auto">
                  We couldn't match that to something MagicBolt AI does. It may simply not be something we cover - try
                  describing the problem differently, browse the directory below, or{" "}
                  <Link to="/contact" className="font-semibold text-[#FF6B00] hover:underline">ask us</Link>.
                </p>
              </div>
            )}

            {faqs.length > 0 && (
              <div className="mt-12">
                <h2 className="text-xl font-bold tracking-tight text-[#0B1533]">Related answers</h2>
                <ul className="mt-4 space-y-3">
                  {faqs.map((f) => (
                    <li key={f.q} className="rounded-xl border border-[#E6E9F1] bg-white p-5">
                      <div className="text-sm font-semibold text-[#0B1533]">{f.q}</div>
                      <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{f.a}</p>
                      <Link to={`/faq#${f.segmentId}`} className="mt-2 inline-block text-xs font-semibold text-[#FF6B00] hover:underline">
                        More on {f.segmentTitle}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Directory - the full relationship map as crawlable text */}
      <section className="py-16 md:py-20 bg-white border-y border-[#E6E9F1]" id="by-problem">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
          <Overline>Browse by problem</Overline>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tighter text-[#0B1533]">
            What problems does MagicBolt AI solve?
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {PROBLEMS.map((pr) => (
              <div key={pr.id} id={`problem-${pr.id}`} className="rounded-2xl border border-[#E6E9F1] bg-[#F4F6FB] p-6">
                <h3 className="text-base font-bold text-[#0B1533]">{pr.question}</h3>
                <ul className="mt-3 space-y-2">
                  {pr.solvedBy.map((s) => (
                    <li key={s.product} className="text-sm text-[#475569] leading-relaxed">
                      <ProductLink id={s.product} /> - {s.role}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" id="by-industry">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tighter text-[#0B1533]">
            Which MagicBolt AI tools suit my industry?
          </h2>
          <p className="mt-3 text-sm text-[#64748B] max-w-2xl">
            Suggested from what each product does - a starting point, not a case study.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((ind) => (
              <div key={ind.id} id={`industry-${ind.id}`} className="rounded-2xl border border-[#E6E9F1] bg-white p-6">
                <h3 className="text-base font-bold text-[#0B1533]">Which tools suit {ind.name.toLowerCase()}?</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{ind.why}</p>
                <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                  {ind.products.map((id) => (
                    <li key={id}>
                      <ProductLink id={id} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-y border-[#E6E9F1]" id="by-user">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
          <Overline>Browse by who you are</Overline>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tighter text-[#0B1533]">
            Where should I start?
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {USER_TYPES.map((u) => (
              <div key={u.id} className="rounded-2xl border border-[#E6E9F1] bg-[#F4F6FB] p-6">
                <h3 className="text-base font-bold text-[#0B1533]">{u.name}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{u.description}</p>
                {u.id !== "local-business" && (
                  <p className="mt-3 text-sm">
                    <span className="text-[#475569]">Start with: </span>
                    <ProductLink id="agency" />
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" id="by-family">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tighter text-[#0B1533]">
            What products are in each family?
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {PRODUCT_FAMILIES.map((fam) => (
              <div key={fam.id} className="rounded-2xl border border-[#E6E9F1] bg-white p-6">
                <h3 className="text-base font-bold text-[#0B1533]">{fam.name}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{fam.description}</p>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {PRODUCTS_KB.filter((p) => p.family === fam.id).map((p) => (
                    <li key={p.id} className="flex items-center gap-1">
                      <ProductLink id={p.id} />
                      <ArrowUpRight className="h-3.5 w-3.5 text-[#CBD2E0]" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-[#64748B]">
            The same map is available as data at{" "}
            <a href="/products.json" className="font-semibold text-[#FF6B00] hover:underline">/products.json</a>. More
            about the company on the <Link to="/about" className="font-semibold text-[#FF6B00] hover:underline">About page</Link>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
